import { useMutation, useQuery } from '@tanstack/react-query'
import type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'

import { getAdminOrgScope } from '#/shared/api/admin-org-scope'
import type { FileResponse } from './models'
import { serviceClient } from '#/shared/api/http-client'

const BASE = '/billing/admin/api/v1/product'

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyResponse = AxiosResponse<any>
type QueryOpts = { query?: Partial<UseQueryOptions<AnyResponse>> & { enabled?: boolean } }
type MutationOpts<V> = { mutation?: UseMutationOptions<AnyResponse, unknown, V> }

function orgParams(extra?: Record<string, unknown>): Record<string, unknown> {
  const organization_id = getAdminOrgScope()
  return { ...(organization_id ? { organization_id } : {}), ...extra }
}

export const getListPaymentMethodsApiV1PaymentMethodsGetQueryKey = (
  params?: Record<string, unknown>,
) => [`/api/v1/payment-methods`, ...(params ? [params] : [])]

export function listPaymentMethodsApiV1PaymentMethodsGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/payment-methods`, { params: orgParams(params), signal })
}

export function useListPaymentMethodsApiV1PaymentMethodsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/payment-methods`, params],
    queryFn: ({ signal }) => listPaymentMethodsApiV1PaymentMethodsGet(params, signal),
    ...options?.query,
  })
}

export const getGetPaymentMethodApiV1PaymentMethodsMethodIdGetQueryKey = (methodId: string) => [
  `/api/v1/payment-methods/${methodId}`,
]

export function useGetPaymentMethodApiV1PaymentMethodsMethodIdGet(
  methodId: string,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: getGetPaymentMethodApiV1PaymentMethodsMethodIdGetQueryKey(methodId),
    queryFn: ({ signal }) =>
      serviceClient.get(`${BASE}/payment-methods/${methodId}`, { params: orgParams(), signal }),
    ...options?.query,
  })
}

export function useCreatePaymentMethodApiV1PaymentMethodsPost(
  options?: MutationOpts<{ data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/payment-methods`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUpdatePaymentMethodApiV1PaymentMethodsMethodIdPatch(
  options?: MutationOpts<{ methodId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ methodId, data }: { methodId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/payment-methods/${methodId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useDeletePaymentMethodApiV1PaymentMethodsMethodIdDelete(
  options?: MutationOpts<{ methodId: string }>,
) {
  return useMutation({
    mutationFn: ({ methodId }: { methodId: string }) =>
      serviceClient.delete(`${BASE}/payment-methods/${methodId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useUploadQrCodeApiV1PaymentMethodsQrUploadPost(
  options?: MutationOpts<{ data: { file: unknown } }>,
) {
  return useMutation({
    mutationFn: ({ data }: { data: { file: unknown } }) => {
      const formData = new FormData()
      formData.append('file', data.file as Blob)
      return serviceClient.post(`${BASE}/payment-methods/qr-upload`, formData, {
        params: orgParams(),
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    ...options?.mutation,
  })
}

export function useListPaymentsApiV1PaymentsGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/payments`, params],
    queryFn: () => serviceClient.get(`${BASE}/payments`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function usePaymentOverviewApiV1PaymentsOverviewGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/payments/overview`, params],
    queryFn: () =>
      serviceClient.get(`${BASE}/payments/overview`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function useCreatePaymentApiV1PaymentsPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/payments`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useGetPaymentApiV1PaymentsPaymentIdGet(paymentId: string, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/payments/${paymentId}`],
    queryFn: () => serviceClient.get(`${BASE}/payments/${paymentId}`, { params: orgParams() }),
    ...options?.query,
  })
}

export function useUpdatePaymentApiV1PaymentsPaymentIdPatch(
  options?: MutationOpts<{ paymentId: string; data: unknown }>,
) {
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data: unknown }) =>
      serviceClient.patch(`${BASE}/payments/${paymentId}`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useDeletePaymentApiV1PaymentsPaymentIdDelete(
  options?: MutationOpts<{ paymentId: string }>,
) {
  return useMutation({
    mutationFn: ({ paymentId }: { paymentId: string }) =>
      serviceClient.delete(`${BASE}/payments/${paymentId}`, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useChargeApiV1PaymentsPaymentIdChargePost(
  options?: MutationOpts<{ paymentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/payments/${paymentId}/charge`, data ?? {}, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useMarkPaidApiV1PaymentsPaymentIdMarkPaidPost(
  options?: MutationOpts<{ paymentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/payments/${paymentId}/mark-paid`, data ?? {}, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useSendInvoiceApiV1PaymentsPaymentIdSendInvoicePost(
  options?: MutationOpts<{ paymentId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ paymentId, data }: { paymentId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/payments/${paymentId}/send-invoice`, data ?? {}, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useSendReceiptApiV1PaymentsTransactionsTransactionIdSendReceiptPost(
  options?: MutationOpts<{ transactionId: string; data?: unknown }>,
) {
  return useMutation({
    mutationFn: ({ transactionId, data }: { transactionId: string; data?: unknown }) =>
      serviceClient.post(`${BASE}/payments/transactions/${transactionId}/send-receipt`, data ?? {}, {
        params: orgParams(),
      }),
    ...options?.mutation,
  })
}

export function useListCardsApiV1CardsGet(params?: Record<string, unknown>, options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/cards`, params],
    queryFn: () => serviceClient.get(`${BASE}/cards`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function useCreateCardApiV1CardsPost(options?: MutationOpts<{ data: unknown }>) {
  return useMutation({
    mutationFn: ({ data }: { data: unknown }) =>
      serviceClient.post(`${BASE}/cards`, data, { params: orgParams() }),
    ...options?.mutation,
  })
}

export function useGetTokenizeBootstrapApiV1CardsTokenizeGet(
  params?: Record<string, unknown>,
  options?: QueryOpts,
) {
  return useQuery<AnyResponse>({
    queryKey: [`/api/v1/cards/tokenize`, params],
    queryFn: () => serviceClient.get(`${BASE}/cards/tokenize`, { params: orgParams(params) }),
    ...options?.query,
  })
}

export function useUploadFilesApiV1FilesPost(
  options?: { mutation?: UseMutationOptions<AxiosResponse<FileResponse[]>, unknown, { data: { files: unknown[] } }> },
) {
  return useMutation({
    mutationFn: ({ data }: { data: { files: unknown[] } }) => {
      const formData = new FormData()
      data.files.forEach((value) => formData.append('files', value as Blob))
      return serviceClient.post<FileResponse[]>(`${BASE}/files`, formData, {
        params: orgParams(),
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    },
    ...options?.mutation,
  })
}

/*
 * QuickBooks OAuth — the console's three admin twins.
 *
 * Named exactly as orval names the product's, so the vendored payment-method
 * dialog and `shared/lib/quickbooks-oauth` compile against this adapter with
 * no edits: the files stay verbatim copies and do not fork from the product.
 * The routes underneath are the admin ones, org-scoped like everything else
 * here, not the tenant `/api/v1` endpoints.
 */

export const getQuickbooksOauthStatusApiV1QuickbooksOauthStatusGetQueryKey = () => [
  `/api/v1/quickbooks/oauth/status`,
]

export function quickbooksOauthStatusApiV1QuickbooksOauthStatusGet(
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/quickbooks/oauth/status`, { params: orgParams(), signal })
}

export function useQuickbooksOauthStatusApiV1QuickbooksOauthStatusGet(options?: QueryOpts) {
  return useQuery<AnyResponse>({
    queryKey: getQuickbooksOauthStatusApiV1QuickbooksOauthStatusGetQueryKey(),
    queryFn: ({ signal }) => quickbooksOauthStatusApiV1QuickbooksOauthStatusGet(signal),
    ...options?.query,
  })
}

export function quickbooksOauthLoginApiV1QuickbooksOauthLoginGet(
  params?: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<AnyResponse> {
  return serviceClient.get(`${BASE}/quickbooks/oauth/login`, {
    params: orgParams(params),
    signal,
  })
}

export function quickbooksOauthDisconnectApiV1QuickbooksOauthDisconnectDelete(): Promise<AnyResponse> {
  return serviceClient.delete(`${BASE}/quickbooks/oauth/disconnect`, { params: orgParams() })
}
